import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/api";
import { Check } from "lucide-react";

const PLANS = [
  {
    name: "Pilot", price: 199, key: "pilot",
    tagline: "For small teams proving AI ROI",
    features: ["1 workspace", "Up to 20 users", "Up to 500 entries / month", "Manual entries",
      "CSV staff cost import", "Decision Centre & Dashboard", "Forecasts", "3 saved scenarios", "CSV export"],
  },
  {
    name: "Growth", price: 699, key: "growth", featured: true,
    tagline: "For businesses tracking AI return across areas",
    features: ["Up to 100 users", "Up to 5,000 entries / month", "All Pilot features",
      "Staff cost API import", "Unlimited scenarios", "P&L summary",
      "Area, role, project & AI tool reports", "Advanced CSV exports", "Priority support"],
  },
  {
    name: "Business", price: 1999, key: "business",
    tagline: "For larger AI rollout decisions",
    features: ["Up to 500 users", "Up to 25,000 entries / month", "All Growth features",
      "Advanced admin controls", "Higher API limits", "Finance reports",
      "Leadership exports", "Priority support", "Custom onboarding"],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  useEffect(() => {
    track("pricing_viewed");
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="max-w-2xl">
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">Pricing</h1>
          <div className="mt-4 max-w-lg space-y-3">
            <p className="text-lg font-medium leading-relaxed text-slate-700">
              AI Value Management plans that reflect decision-maker value.
            </p>
            <p className="text-base leading-relaxed text-slate-600">
              Start on any plan with a 14-day free trial. No credit card required.
            </p>
            <p className="text-base font-medium leading-relaxed text-slate-700">
              Use sample data or your own. Cancel anytime.
            </p>
          </div>
        </div>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PLANS.map((p) => (
            <div
              key={p.key}
              className={`relative flex flex-col rounded-xl border bg-white p-7 shadow-sm ${
                p.featured ? "border-navy ring-1 ring-navy" : "border-slate-200"
              }`}
              data-testid={`plan-${p.key}`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-7 rounded-full bg-editorial px-3 py-1 text-xs font-medium text-white">
                  Most popular
                </span>
              )}
              <h2 className="font-heading text-xl font-semibold text-slate-900">{p.name}</h2>
              <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="kpi-number text-4xl font-semibold text-slate-900">${p.price.toLocaleString()}</span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <Button
                className={`mt-6 ${p.featured ? "bg-editorial hover:bg-editorial/90" : ""}`}
                variant={p.featured ? "default" : "outline"}
                onClick={() => { track("plan_selected", { plan: p.key }); navigate("/signup"); }}
                data-testid={`plan-${p.key}-cta`}
              >
                Start Free Trial
              </Button>
              <ul className="mt-7 space-y-3 text-sm text-slate-600">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-10 text-sm text-slate-500">
          Customers can cancel anytime. Access continues until the end of the paid billing period.
        </p>
      </section>
      <Footer />
    </div>
  );
}
